import { useState } from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import { Box, Link, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckIcon from '@material-ui/icons/Check';
import CancelIcon from '@material-ui/icons/Cancel';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { ConfirmDialog, FileUploadModal, StandardDialog } from '@roc/ui';
import { useBorrowerBaseballCardStore } from '@roc/feature-borrowers';
import { RowAction, RowActionsMenu } from './rowActionsMenu';
import { VerifyRecordDialog } from './verifyRecordDialog';
import { UnverifyRecordDialog } from './unverifyRecordDialog';
import { useCenteredDialogStyles } from './centeredDialogStyles';
import { StatusPill, TrackRecordFilterChips } from './trackRecordTabControls';
import { TrackRecordEntityForm } from './trackRecordEntityForm';

const mapEntity = (entity: any, properties: any[], index: number) => {
  const source = entity.verificationSource && entity.verificationSource !== 'null' ? entity.verificationSource : '—';
  return {
    id: entity.borrowerEntityBorrowerId ?? entity.entityName,
    // id falls back to entityName for unlinked entities, so it can repeat; React keys need to be unique.
    rowKey: `${entity.borrowerEntityBorrowerId ?? entity.entityName}-${index}`,
    raw: entity,
    name: entity.entityName || '—',
    borrowerRole: '—',
    properties: properties.filter((property) => property.entityName === entity.entityName).length,
    status: entity.entityVerified === 'No' ? 'needsInput' : 'verified',
    source,
  };
};

// Used by the sidebar nav badges — same verified/needsInput split as this tab.
export const getEntitiesStatusCounts = (verificationStore: any) => {
  const entities = verificationStore?.entities ?? [];
  const needsInput = entities.filter((entity: any) => entity.entityVerified === 'No').length;
  return { verified: entities.length - needsInput, needsInput };
};

// Same gating trackRecordEntities.tsx uses to decide which row actions show.
const getEntityActionFlags = (entity: any, isInternal: boolean) => ({
  canEdit: isInternal && entity.entityVerified === 'No',
  canVerify: isInternal && entity.entityVerified === 'No',
  canUnverify: isInternal && entity.entityVerified === 'Yes',
  canRemove: isInternal && entity.entityVerified === 'No' && entity.isEntityPresentInLoan !== 'true',
});

export const EntitiesTab = observer(({
  classes,
  verificationStore,
  isInternal,
}: {
  classes: Record<string, string>;
  verificationStore?: any;
  isInternal?: boolean;
}) => {
  const { borrowerBaseballCardStore } = useBorrowerBaseballCardStore();
  const centeredDialogClasses = useCenteredDialogStyles();
  // Hide Verify/Unverify/Remove outside the internal store where they'd no-op silently.
  const canManage = !!isInternal && !!borrowerBaseballCardStore?.isTrackRecordVerificationEnabled;
  const [expandedEntity, setExpandedEntity] = useState<string | null>(null);
  const [entitiesFilter, setEntitiesFilter] = useState<'all' | 'needsInput' | 'verified'>('all');
  const [sortColumn, setSortColumn] = useState<'name' | 'properties' | 'status' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [uploadTarget, setUploadTarget] = useState<any | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<any | null>(null);
  const [unverifyTarget, setUnverifyTarget] = useState<any | null>(null);
  const [removeTarget, setRemoveTarget] = useState<any | null>(null);

  const entities = (verificationStore?.entities ?? [])
    .filter(Boolean)
    .map((entity, index) => mapEntity(entity, verificationStore?.properties ?? [], index));
  const needsInputCount = entities.filter((entity) => entity.status === 'needsInput').length;
  const verifiedCount = entities.filter((entity) => entity.status === 'verified').length;
  const visibleEntities = entities.filter((entity) =>
    entitiesFilter === 'all' ? true : entity.status === entitiesFilter
  );

  const handleSort = (column: 'name' | 'properties' | 'status') => {
    if (sortColumn === column) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedEntities = sortColumn
    ? [...visibleEntities].sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];
        if (aValue === bValue) return 0;
        const comparison = aValue < bValue ? -1 : 1;
        return sortDirection === 'asc' ? comparison : -comparison;
      })
    : [...visibleEntities].sort((a, b) =>
        a.status === b.status ? 0 : a.status === 'verified' ? -1 : 1
      );

  const sortIndicator = (column: 'name' | 'properties' | 'status') =>
    sortColumn === column ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '';

  // Single overview re-fetch instead of a separate entities endpoint — keeps
  // properties/entities/licenses/summary all refreshed together.
  const refreshEntities = () => {
    verificationStore?.loadFromOverview?.();
  };

  const taskBodyFor = (entity: any) => ({
    sectionProcess: 'TRACK_RECORD_REVIEW',
    objectType: 'BORROWER_ENTITY_BORROWER',
    taskTemplateType: 'BORROWER_ENTITY_BORROWER',
    objectId: entity.borrowerEntityBorrowerId,
  });

  // Docs for the expanded row; currentTasks is refreshed by getTasks and by createTaskWithFile.
  const uploadedDocs = (verificationStore?.currentTasks ?? []).filter((task: any) => task?.originalFileName);

  const toggleExpand = (entity: any) => {
    const expanding = expandedEntity !== entity.id;
    setExpandedEntity(expanding ? entity.id : null);
    if (expanding && entity.raw.borrowerEntityBorrowerId) verificationStore?.getTasks(taskBodyFor(entity.raw));
  };

  const handleUpload = async (files: any[]) => {
    const entity = uploadTarget;
    setUploadTarget(null);
    if (!files?.[0] || !entity) return;
    const uploaded = await verificationStore?.createTaskWithFile(taskBodyFor(entity), files[0]);
    if (uploaded?.fileDownloadId) {
      setExpandedEntity(entity.borrowerEntityBorrowerId ?? entity.entityName);
      verificationStore?.globalStore?.notificationStore?.showSuccessNotification({ message: 'Document uploaded successfully' });
    }
  };

  const handleVerify = async (verificationSource: string, othersReason: string) => {
    const entity = verifyTarget;
    setVerifyTarget(null);
    if (!entity) return;
    await borrowerBaseballCardStore?.updateVerifyEntity(
      entity.borrowerEntityBorrowerId,
      entity.entityVerified === 'Yes',
      verificationSource,
      borrowerBaseballCardStore?.loanId,
      othersReason,
    );
    refreshEntities();
  };

  const handleUnverify = async (reason: string) => {
    const entity = unverifyTarget;
    setUnverifyTarget(null);
    if (!entity) return;
    await borrowerBaseballCardStore?.updateVerifyEntity(
      entity.borrowerEntityBorrowerId,
      entity.entityVerified === 'Yes',
      reason,
      borrowerBaseballCardStore?.loanId,
    );
    refreshEntities();
  };

  // handleEditExistingTrackRecordEntity builds currentDocumentsToUpload from currentTasks, so load them first.
  const handleEdit = async (entity: any) => {
    if (!verificationStore) return;
    verificationStore.currentTasks = [];
    if (entity.borrowerEntityBorrowerId) await verificationStore.getTasks(taskBodyFor(entity));
    const index = (verificationStore.entities ?? []).indexOf(entity);
    verificationStore.handleEditExistingTrackRecordEntity(entity, index);
  };

  // Narrow close: resetStore() would also clear the GC/broker answers form.
  const closeEdit = () => {
    runInAction(() => {
      if (verificationStore) verificationStore.isEntityFormActive = false;
    });
    verificationStore?.trackRecordEntityFormStore?.reset();
  };

  const handleSaveEdit = async () => {
    await verificationStore?.saveTrackRecordEntity();
    // saveTrackRecordEntity silently skips the request when the form is invalid; keep the dialog open.
    if (!verificationStore?.trackRecordEntityFormStore?.form?.meta?.isValid) return;
    closeEdit();
    refreshEntities();
  };

  const handleRemove = async () => {
    const entity = removeTarget;
    setRemoveTarget(null);
    if (!entity) return;
    await borrowerBaseballCardStore?.removeEntity(entity.borrowerEntityBorrowerId);
    refreshEntities();
  };

  return (
    <>
      <Typography variant="h5" className={classes.overviewTitle}>
        Entities
      </Typography>
      <Typography className={classes.overviewSubtitle}>
        Ownership we could confirm for the LLCs on this track record.
      </Typography>

      <TrackRecordFilterChips
        classes={classes}
        selected={entitiesFilter}
        onSelect={setEntitiesFilter}
        total={entities.length}
        needsInputCount={needsInputCount}
        verifiedCount={verifiedCount}
      />

      <Box className={classes.table}>
        <Box className={`${classes.entityGridRow} ${classes.tableHeaderRow}`} style={{ cursor: 'default' }}>
          <Typography className={classes.tableHeaderCell} style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
            Entity{sortIndicator('name')}
          </Typography>
          <Typography className={classes.tableHeaderCell}>Borrower Role</Typography>
          <Typography className={classes.tableHeaderCell} style={{ cursor: 'pointer' }} onClick={() => handleSort('properties')}>
            Properties{sortIndicator('properties')}
          </Typography>
          <Typography className={classes.tableHeaderCell} style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
            Status{sortIndicator('status')}
          </Typography>
          <Typography className={classes.tableHeaderCell}>Source</Typography>
          <span />
          <span />
        </Box>
        {sortedEntities.map((entity) => {
          const isExpanded = expandedEntity === entity.id;
          const flags = getEntityActionFlags(entity.raw, canManage);
          const actions: RowAction[] = [
            ...(flags.canEdit ? [{ key: 'edit', label: 'Edit', icon: EditIcon, onClick: () => handleEdit(entity.raw) }] : []),
            { key: 'upload', label: 'Upload Document', icon: CloudUploadIcon, onClick: () => setUploadTarget(entity.raw) },
            ...(flags.canVerify ? [{ key: 'verify', label: 'Verify Entity', icon: CheckIcon, onClick: () => setVerifyTarget(entity.raw) }] : []),
            ...(flags.canUnverify ? [{ key: 'unverify', label: 'Unverify Entity', icon: CancelIcon, onClick: () => setUnverifyTarget(entity.raw) }] : []),
            ...(flags.canRemove ? [{ key: 'remove', label: 'Remove', icon: DeleteIcon, onClick: () => setRemoveTarget(entity.raw) }] : []),
          ];
          return (
            <Box
              key={entity.rowKey}
              className={`${classes.entityRowContainer} ${
                entity.status === 'needsInput' ? classes.entityRowNeedsInput : ''
              }`}
            >
              <Box
                className={classes.entityGridRow}
                onClick={() => toggleExpand(entity)}
              >
                <Typography className={classes.tableCellPrimary}>{entity.name}</Typography>
                <Typography className={classes.tableCell}>{entity.borrowerRole}</Typography>
                <Typography className={classes.tableCell}>{entity.properties}</Typography>
                <StatusPill classes={classes} status={entity.status} />
                <Typography className={classes.tableCell}>{entity.source}</Typography>
                <RowActionsMenu actions={actions} />
                <ExpandMoreIcon
                  className={classes.expandChevron}
                  style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}
                />
              </Box>
              {isExpanded && (
                <Box className={classes.entityExpandContent}>
                  <Typography className={classes.entityDetailLabel}>Documents</Typography>
                  <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 16 }}>
                    {uploadedDocs.length > 0
                      ? uploadedDocs.map((task) => (
                        <Link
                          key={task.taskId}
                          component="button"
                          onClick={() => verificationStore?.downloadTaskFile(task.taskId, task.originalFileName, task.fileExtension)}
                        >
                          {task.originalFileName}
                        </Link>
                      ))
                      : <Typography className={classes.entityDetailValue}>No documents uploaded</Typography>}
                  </Box>

                  {entity.status === 'needsInput' && (
                    <>
                      <Box className={classes.entityActionsRow}>
                        {uploadedDocs.length === 0 && (
                          <span className={classes.entityNoDocumentLink}>I don't have this document</span>
                        )}
                        <button
                          type="button"
                          className={classes.uploadSupportingDocButton}
                          onClick={() => setUploadTarget(entity.raw)}
                        >
                          Upload Supporting Document
                        </button>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      <StandardDialog
        open={!!verificationStore?.isEntityFormActive}
        handleClose={closeEdit}
        maxWidth="sm"
        fullWidth
        removeContentBox
        dialogContent={
          <TrackRecordEntityForm store={verificationStore} onCancel={closeEdit} onSave={handleSaveEdit} />
        }
      />

      <FileUploadModal
        dialogClasses={centeredDialogClasses}
        open={uploadTarget != null}
        title="Upload Document"
        onClose={() => setUploadTarget(null)}
        onUpload={handleUpload}
      />

      <VerifyRecordDialog
        open={verifyTarget != null}
        title="Verify Entity"
        recordLabel="entity"
        sourceOptions={['Bizapedia', 'Open Corporates', 'Entity Docs', 'Other']}
        onClose={() => setVerifyTarget(null)}
        onConfirm={handleVerify}
      />

      <UnverifyRecordDialog
        open={unverifyTarget != null}
        title="Unverify Entity"
        recordLabel="entity"
        onClose={() => setUnverifyTarget(null)}
        onConfirm={handleUnverify}
      />

      <ConfirmDialog
        open={removeTarget != null}
        title="Confirmation"
        confirmButtonText="Yes"
        body="Are you sure you want to remove this entity?"
        handleClose={() => setRemoveTarget(null)}
        handleCancel={() => setRemoveTarget(null)}
        handleConfirm={handleRemove}
      />
    </>
  );
});
